import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import qs from 'qs';

import { get, post } from '@network/api';
import { getErrorMessage } from '@lib/handle-error';
import { useCheckoutStore } from '@stores/useCheckoutStore';
import {
  DataDetailCheckout,
  DataPayment,
  ItemPayment,
  PaymentMethodKey,
  PaymentMethodResponse,
} from '@pages/types';

interface GenerateSessionPayload {
  type: string;
  service_id: string;
  customer_no: string;
  payment_method_id: number;
}

export interface GenerateSessionResponse {
  data?: {
    h2h_payment_info?: {
      web_redirect_url?: string;
    };
  };
}

interface ResolvedPaymentMethod {
  type: string;
  id: number;
  service_id: string;
}

// Normalize 'card' -> 'cards' so it matches the payment-method response key
const normalizeCategory = (category: string): string =>
  category === 'card' ? 'cards' : category;

// The invoice detail response only tells us the payment *category* (e.g. "ewallet",
// "va") plus, for VA, which bank. It never carries back the specific
// payment_method_id/service_id that was originally submitted. When the URL doesn't
// have those (e.g. the page was opened fresh instead of coming from an in-session
// method selection), re-derive them from the payment-method list.
async function resolveSelectedPaymentMethod(
  invoiceId: string,
  payment?: DataPayment
): Promise<ResolvedPaymentMethod | null> {
  const rawType = payment?.type;
  if (!rawType) return null;

  const category = normalizeCategory(rawType.toLowerCase()) as PaymentMethodKey;

  const response = await get<PaymentMethodResponse>(
    `/checkout/v1/payment-method?invoice_number=${invoiceId}`
  );
  const items: ItemPayment[] = response?.data?.[category] || [];
  if (!items.length) return null;

  if (category === 'va') {
    const bankCode = payment?.va?.bank?.toLowerCase();
    const matched = items.find((item) => {
      const itemCode = item.code?.toLowerCase();
      const itemName = item.name?.toLowerCase();
      return bankCode && (itemCode === bankCode || itemName === bankCode);
    });
    const item = matched || items[0];
    return {
      type: item.type || rawType,
      id: item.id,
      service_id: item.service_id,
    };
  }

  // For ewallet/cards/direct_debit/qris the invoice detail doesn't expose which
  // specific provider was used, so fall back to the first item in that category.
  const item = items[0];
  return {
    type: item.type || rawType,
    id: item.id,
    service_id: item.service_id,
  };
}

export const useGenerateNewSession = (
  data?: DataDetailCheckout | null,
  onSuccess?: (response: GenerateSessionResponse) => void
): UseMutationResult<GenerateSessionResponse, unknown, void> => {
  const location = useLocation();
  const { submittedCustomerNo } = useCheckoutStore();

  return useMutation({
    mutationFn: async () => {
      const {
        id,
        type: typeFromUrl,
        payment_method_id: methodIdFromUrl,
        service_id: serviceIdFromUrl,
      } = qs.parse(location.search, { ignoreQueryPrefix: true }) as {
        id?: string;
        type?: string;
        payment_method_id?: string;
        service_id?: string;
      };

      if (!id) throw new Error('Invoice ID is missing');

      let type = typeFromUrl;
      let paymentMethodId = methodIdFromUrl;
      let serviceId = serviceIdFromUrl;

      if (!type || !paymentMethodId) {
        const resolved = await resolveSelectedPaymentMethod(id, data?.payment);
        if (!resolved) {
          throw new Error(
            'Payment method is missing. Please select a payment method again.'
          );
        }
        type = resolved.type;
        paymentMethodId = String(resolved.id);
        serviceId = resolved.service_id;
      }

      const payload: GenerateSessionPayload = {
        type,
        service_id: serviceId || '',
        customer_no: submittedCustomerNo || '',
        payment_method_id: Number(paymentMethodId),
      };

      return post<GenerateSessionResponse>(
        `/checkout/v1/payment-method?invoice_number=${id}`,
        payload
      );
    },
    onSuccess: (response) => {
      onSuccess?.(response);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
};
